import React from 'react';
import { useNavigate } from 'react-router-dom';

interface OrganizerCardProps {
  name: string;
  email: string;
  avatarUrl?: string | null;
  organizerId?: string;
}

/**
 * OrganizerCard component displays the organizer's avatar (or initials) together with their name/email.
 * It gracefully handles missing `name`, `avatarUrl`, or malformed email values.
 */
export default function OrganizerCard({ name, email, avatarUrl, organizerId }: OrganizerCardProps) {
  const navigate = useNavigate();
  
  // Guard against empty or malformed email values
  const safeEmail = typeof email === 'string' ? email : '';
  const displayName = name?.trim() || safeEmail.split('@')[0] || 'Unknown';

  // Use the first character of the display name for initials
  const initials = displayName.charAt(0).toUpperCase();

  // Basic error handling for image loading failures
  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.style.display = 'none';
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8">
      <h2 className="text-lg font-bold text-gray-900 mb-5">Organizer</h2>
      <div className="flex items-center gap-4 mb-5">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={displayName}
            className="w-14 h-14 rounded-full object-cover"
            onError={handleImgError}
          />
        ) : (
          <div className="w-14 h-14 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white text-lg font-bold">{initials}</span>
          </div>
        )}
        <div>
          <p className="text-sm font-semibold text-gray-900">{displayName}</p>
          <p className="text-xs text-gray-500 mt-0.5">Event Organizer</p>
        </div>
      </div>
      
      {organizerId && (
        <button
          onClick={() => navigate(`/store/${organizerId}`)}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-teal-50 hover:bg-teal-100 text-teal-600 rounded-lg font-medium transition-colors whitespace-nowrap"
        >
          <i className="ri-store-3-line text-lg"></i>
          <span>Visit Store</span>
        </button>
      )}
    </div>
  );
}
